import React, { useEffect, useState } from "react";
import {
  authFetch,
  clearAuthTokens,
  logoutUser,
} from "../../utils/auth";
import { Link, useNavigate } from "react-router-dom";

import "./Admin.css";
import "./AdminMessages.css";
import "./AdminMessages.mobile.css";

const AdminMessages = () => {
  const [messages,  setMessages]  = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [error,     setError]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [deleting,  setDeleting]  = useState(null);
  const [username,  setUsername]  = useState("");
  const [authChecked, setAuthChecked] = useState(false);

  const navigate = useNavigate();

  // ── Auth ─────────────────────────────────────────────────
	useEffect(() => {
	const check = async () => {
		try {
		const res = await authFetch(
			"/api/dashboard/user/"
		);

		if (!res.ok) {
			throw new Error("Authentication failed");
		}

		const data = await res.json();

		setUsername(data.username);
		setAuthChecked(true);

		} catch {
		clearAuthTokens();

		navigate(
			"/login",
			{ replace: true }
		);
		}
	};

	check();
	}, [navigate]);

// ── Chargement messages ───────────────────────────────────
	useEffect(() => {
		if (!authChecked) return;

		const loadMessages = async () => {
			try {
			const res = await authFetch(
				"/api/contact/admin/messages/"
			);

			if (!res.ok) {
				throw new Error(
				"Impossible de charger les messages"
				);
			}

			const data = await res.json();

			setMessages(
				Array.isArray(data) ? data : []
			);

			} catch (err) {
			setError(err.message);
			} finally {
			setLoading(false);
			}
		};

		loadMessages();
	}, [authChecked]);

// ── Suppression ───────────────────────────────────────────
	const handleDelete = async (id) => {
		if (!window.confirm("Supprimer ce message ?")) {
			return;
		}

		setDeleting(id);

		try {
			const res = await authFetch(
			`/api/contact/admin/messages/${id}/`,
			{
				method: "DELETE",
			}
			);

			if (!res.ok) {
			throw new Error(
				"Erreur lors de la suppression."
			);
			}

			setMessages((prev) =>
			prev.filter((m) => m.id !== id)
			);

			if (selected?.id === id) {
			setSelected(null);
			}

		} catch (err) {
			alert(err.message);
		} finally {
			setDeleting(null);
		}
	};

  // ── Logout ────────────────────────────────────────────────
  const handleLogout = async () => {
	await logoutUser();
	navigate("/login", { replace: true });
  };

  if (!authChecked) return null;

  if (loading) return (
    <div className="adm-loading">
      <span>CHARGEMENT</span>
      <span className="adm-loading__dots">...</span>
    </div>
  );

  if (error) return (
    <div className="adm-error">
      <span className="adm-error__code">ERR</span>
      <p className="adm-error__msg">{error}</p>
      <Link to="/adminHome" className="adm-btn adm-btn--ghost">← DASHBOARD</Link>
    </div>
  );

  return (
    <div className="msgs-page">

      {/* ── HEADER ── */}
      <header className="adm-header">
        <div className="adm-header__left">
          <Link to="/adminHome" className="adm-header__back">← DASHBOARD</Link>
          <h1 className="adm-header__title">
            ADMIN<span className="adm-header__slash">/</span>MESSAGES
          </h1>
        </div>
        <div className="adm-header__right">
          <span className="adm-header__user">{username}</span>
          <button className="adm-header__logout" onClick={handleLogout}>Déconnexion</button>
          <div className="adm-header__badge">
            <span className="adm-header__badge-num">{messages.length}</span>
            <span className="adm-header__badge-lbl">MSG</span>
          </div>
        </div>
      </header>

      {/* ── LAYOUT ── */}
      <div className="msgs-layout">

        {/* ── LISTE ── */}
        <aside className="msgs-list">
          {messages.length === 0 ? (
            <div className="msgs-empty">
              <span className="msgs-empty__icon">✦</span>
              <p>AUCUN MESSAGE</p>
            </div>
          ) : messages.map(msg => (
            <div
              key={msg.id}
              className={`msgs-item ${selected?.id === msg.id ? "msgs-item--active" : ""}`}
              onClick={() => setSelected(msg)}
            >
              <div className="msgs-item__top">
                <span className="msgs-item__name">{msg.name}</span>
                <span className="msgs-item__date">
                  {msg.created_at
                    ? new Date(msg.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })
                    : "—"}
                </span>
              </div>
              <div className="msgs-item__subject">{msg.subject}</div>
              <div className="msgs-item__preview">
                {msg.message.length > 80 ? msg.message.slice(0, 80) + "…" : msg.message}
              </div>
            </div>
          ))}
        </aside>

        {/* ── DÉTAIL ── */}
        <main className="msgs-detail">
          {!selected ? (
            <div className="msgs-placeholder">
              <span className="msgs-placeholder__icon">✦</span>
              <p>SÉLECTIONNE UN MESSAGE</p>
            </div>
          ) : (
            <div className="msgs-msg">

              <div className="msgs-msg__header">
                <div className="msgs-msg__from">
                  <span className="msgs-msg__label">DE</span>
                  <span className="msgs-msg__name">{selected.name}</span>
                </div>
                <button
                  className="msgs-msg__delete"
                  onClick={() => handleDelete(selected.id)}
                  disabled={deleting === selected.id}
                >
                  {deleting === selected.id ? "..." : "✕ SUPPRIMER"}
                </button>
              </div>

              <div className="msgs-divider"><span>✦</span></div>

              <div className="msgs-meta">
                <div className="msgs-meta__cell">
                  <span className="msgs-meta__label">EMAIL</span>
                  <a href={`mailto:${selected.email}`} className="msgs-meta__value msgs-meta__link">{selected.email}</a>
                </div>
                <div className="msgs-meta__cell">
                  <span className="msgs-meta__label">TÉLÉPHONE</span>
                  <a href={`tel:${selected.phone}`} className="msgs-meta__value msgs-meta__link">{selected.phone || "—"}</a>
                </div>
                <div className="msgs-meta__cell">
                  <span className="msgs-meta__label">DATE</span>
                  <span className="msgs-meta__value">
                    {selected.created_at
                      ? new Date(selected.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })
                      : "—"}
                  </span>
                </div>
                <div className="msgs-meta__cell">
                  <span className="msgs-meta__label">SUJET</span>
                  <span className="msgs-meta__value">{selected.subject}</span>
                </div>
              </div>

              <div className="msgs-divider"><span>✦</span></div>

              <div className="msgs-body">
                <span className="msgs-msg__label">MESSAGE</span>
                <p className="msgs-body__text">{selected.message}</p>
              </div>

              <div className="msgs-actions">
                <a href={`mailto:${selected.email}`} className="adm-btn adm-btn--accent">✉ RÉPONDRE</a>
                <button className="adm-btn adm-btn--ghost" onClick={() => setSelected(null)}>← FERMER</button>
              </div>

            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminMessages;